import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

import Icon from '@frolight/components/Icon';
import IconButton from '@frolight/components/IconButton';
import TemperatureChart from '@frolight/components/TemperatureChart';
import useRenderValuesInPreferredUnit from '@frolight/hooks/useRenderValuesInPreferredUnit';
import DateUtils from '@frolight/utils/DateUtils';
import MathUtils from '@frolight/utils/MathUtils';
import useLocalizedFormattedText from '@frolight/hooks/useLocalizedFormattedText';

import useChartData from './../../hooks/useChartData';
import useConnectedPeripheralsData from './../../hooks/useConnectedPeripheralsData';
import usePeripheralInfoData from './../../hooks/usePeripheralInfoData';
import useRecentTemperatureTelemetryData from './../../hooks/useRecentTemperatureTelemetryData';

const GatewayDetailConnectedUnitDetails = ({ gateway, unit }) => {
  const { lftext } = useLocalizedFormattedText();

  const NUMBER_OF_DAYS_IN_TIME_RANGE = 7;

  const { renderTemperature } = useRenderValuesInPreferredUnit();

  const [ timeRange, setTimeRange ] = useState(() => {
    return {
      from: new Date(
        new Date(
          new Date().setDate(
            new Date().getDate() - NUMBER_OF_DAYS_IN_TIME_RANGE + 1
          )
        ).setHours(0, 0, 0, 0)
      ).getTime(),
      to: new Date().getTime()
    };
  });
  const [ connectedTemperatureSensors, setConnectedTemperatureSensors ] = useState(null);
  const [ dryTempSensorDeviceId, setDryTempSensorDeviceId ] = useState(null);
  const [ wetTempSensorDeviceId, setWetTempSensorDeviceId ] = useState(null);
  const [ selectedTemperatureSensorDeviceId, setSelectedTemperatureSensorDeviceId ] = useState(null);
  const [ contactorDeviceId, setContactorDeviceId ] = useState(null);
  const [ unitTypeLabel, setUnitTypeLabel ] = useState(null);
  const [ connectedPeripheralsDeviceIds, setConnectedPeripheralsDeviceIds ] = useState(null);
  const [ relativeHumidity, setRelativeHumidity ] = useState(null);

  const { loading: connectedPeripheralsDataLoading, data: connectedPeripheralsData, error: connectedPeripheralsDataError } = useConnectedPeripheralsData(gateway?.id, unit?.deviceId );
  const { loading: peripheralInfoDataLoading, data: peripheralInfoData, error: peripheralInfoDataError } = usePeripheralInfoData(connectedPeripheralsDeviceIds);
  const { loading: chartLoading, data: chartData, error: chartError } = useChartData(gateway?.id, unit?.deviceId, selectedTemperatureSensorDeviceId, contactorDeviceId, timeRange);
  const { loading: recentTemperatureTelemetryDataLoading, data: recentTemperatureTelemetryData, error: recentTemperatureTelemetryDataError } = useRecentTemperatureTelemetryData(gateway.id, unit.deviceId, dryTempSensorDeviceId, wetTempSensorDeviceId);

  useEffect(() => {
    let mounted = true;

    const label = 
      unit.type === 'master'
        ? lftext('pages.device_detail.master')
        : unit.type === 'slave'
          ? lftext('pages.device_detail.slave')
          : unit.type;
    mounted && setUnitTypeLabel(label);

    () => {
      mounted = false;
    }
  }, [ unit ]);

  useEffect(() => {
    let mounted = true;

    if (connectedPeripheralsData) {
      if (mounted) {
        setConnectedPeripheralsDeviceIds((connectedPeripheralsData || []).map(s => s.deviceId));
      }
    }

    return () => {
      mounted = false;
    };
  }, [ connectedPeripheralsData ]);

  useEffect(() => {
    let mounted = true;

    if (peripheralInfoData && connectedPeripheralsData) {
      const contactorPeripherals = (connectedPeripheralsData || []).filter((peripheral) => {
        return peripheral.category === 'contactor';
      });
      if (contactorPeripherals.length) {
        if (mounted) setContactorDeviceId(contactorPeripherals[0].deviceId);
      }

      const temperatureSensors = (connectedPeripheralsData || [])
        .filter((peripheral) => {
          // Filter to only use temperature sensors
          return peripheral.category === 'temperature';
        })
        .map(peripheral => {
          const sensorInfoObj = peripheralInfoData.find(p => p.deviceId === peripheral.deviceId);
          if (sensorInfoObj) {
            peripheral.peripheralType = sensorInfoObj.type;
          } else {
            peripheral.peripheralType = 'sensor_temperature_default';
          }
          return peripheral;
        });
        
      if (mounted) setConnectedTemperatureSensors(temperatureSensors);

      if (temperatureSensors.length) {
        if (mounted) setSelectedTemperatureSensorDeviceId(temperatureSensors[0].deviceId);
      }
    }

    return () => {
      mounted = false;
    }
  }, [ peripheralInfoData, connectedPeripheralsData ]);

  useEffect(() => {
    let mounted = true;

    if (connectedTemperatureSensors) {
      const activeSensors = connectedTemperatureSensors.filter(s => s.status === 'active');
      if (activeSensors.length) {
        const includedSensorTypes = [...new Set(activeSensors.map(s => s.peripheralType))];
        if (
          includedSensorTypes.includes('sensor_temperature_default') ||
          includedSensorTypes.includes('sensor_temperature_wetbulb')
        ) {
          for (const s of activeSensors) {
            if (!dryTempSensorDeviceId && s.peripheralType === 'sensor_temperature_default') {
              if (mounted) setDryTempSensorDeviceId(s.deviceId);
            } else if (!wetTempSensorDeviceId && s.peripheralType === 'sensor_temperature_wetbulb') {
              if (mounted) setWetTempSensorDeviceId(s.deviceId);
            }
          }
        }
      }

    }

    return () => {
      mounted = false;
    }
  }, [ connectedTemperatureSensors ]);

  useEffect(() => {
    let mounted = true;

    const calculateRelativeHumidity = () => {
      // Formula taken from https://www.1728.org/relhum.htm
      const currentWetbulbTemp = recentTemperatureTelemetryData.wetbulb.temperature.current;
      const currentDryTemp = recentTemperatureTelemetryData.dry.temperature.current;
      const ed = 6.112 * Math.pow(Math.E, ((17.502 * currentDryTemp) / (240.97 + currentDryTemp)));
      const ew = 6.112 * Math.pow(Math.E, ((17.502 * currentWetbulbTemp) / (240.97 + currentWetbulbTemp)));
      const nConstant = 0.6687451584;

      const relativeHumidity = (
        ew - (
          nConstant * (1 + (0.00115 * currentWetbulbTemp)) * (currentDryTemp - currentWetbulbTemp)
        )
      ) / ed;

      setRelativeHumidity(relativeHumidity);
    };

    if (
      recentTemperatureTelemetryData &&
      recentTemperatureTelemetryData?.dry !== undefined &&
      recentTemperatureTelemetryData?.wetbulb !== undefined
    ) {
      calculateRelativeHumidity();
    }

    return () => {
      mounted = false;
    }
  }, [ recentTemperatureTelemetryData ]);

  const onNavigateBeforeClick = () => {
    const currentFirstDay = new Date(timeRange.from);
    currentFirstDay.setDate(currentFirstDay.getDate() - 1);
    const newStartTime = new Date(currentFirstDay);
    newStartTime.setDate(newStartTime.getDate() - NUMBER_OF_DAYS_IN_TIME_RANGE + 1);
    newStartTime.setHours(0, 0, 0, 0);
    const newEndTime = new Date(currentFirstDay);
    newEndTime.setHours(23, 59, 59, 999);

    setTimeRange({
      from: newStartTime.getTime(),
      to: newEndTime.getTime()
    });
  };

  const onNavigateNextClick = () => {
    const currentFirstDay = new Date(timeRange.from);
    currentFirstDay.setDate(currentFirstDay.getDate() - 1);
    const newStartTime = new Date(currentFirstDay);
    newStartTime.setDate(newStartTime.getDate() + NUMBER_OF_DAYS_IN_TIME_RANGE + 1);
    newStartTime.setHours(0, 0, 0, 0);
    let newEndTime = new Date(newStartTime.getTime());
    newEndTime.setDate(newEndTime.getDate() + NUMBER_OF_DAYS_IN_TIME_RANGE - 1);
    newEndTime.setHours(23, 59, 59, 999);
    if (newEndTime.getTime() > new Date().getTime()) {
      newEndTime = new Date();
    }

    setTimeRange({
      from: newStartTime.getTime(),
      to: newEndTime.getTime()
    });
  };

  const renderError = (error) => {
    let feedbackMessage = lftext('pages.device_detail.errors.default_feedback_message');
    let feedbackStatus = 'error'

    if (error.type) {
      switch (error.type) {
        case 'NO_TEMPERATURE_DATA_FOR_PERIOD':
          feedbackMessage = lftext('pages.device_detail.errors.no_temperature_data_message');
          feedbackStatus = 'warning'
          break;
        case 'NO_SENSORS_FOR_UNIT':
          feedbackMessage = lftext('pages.device_detail.errors.no_sensors_for_unit_message');
          feedbackStatus = 'warning'
        default:
          break;
      }
    }

    return (
      <div className={ `feedback_message  feedback_message--${feedbackStatus}` }>
        { feedbackMessage }
      </div>
    );
  };

  const getSensorTypeLabel = (peripheralType) => {
    switch (peripheralType) {
      case 'sensor_temperature_wetbulb':
        return lftext('pages.device_detail.wet_bulb_temperature');
      case 'sensor_temperature_default':
        return lftext('pages.device_detail.dry_temperature');
      default:
        return lftext('pages.device_detail.unknown_sensor');
    }
  }
  
  const getSensorStatusLabel = (peripheralStatus) => {
    switch (peripheralStatus) {
      case 'active':
        return lftext('common.status.active');
      case 'inactive':
        return lftext('common.status.inactive');
      default:
        return lftext('common.status.unknown_status');
    }
  }

  return (
    <>
      {
        connectedPeripheralsDataLoading && (
          <div className="loader">
            <p>
              {lftext('common.loading')}
            </p>
          </div>
        )
      }
      {
        connectedPeripheralsDataError && (
          <div className="feedback_message  feedback_message--error">
            {lftext('pages.device_detail.errors.error_getting_peripherals')}
          </div>
        )
      }
      {
        connectedPeripheralsData && (
          <article className="summary_details">
            <header className="summary_details__header">
              <h3>{ unit.name }</h3>
            </header>
            <div className="summary_details__summary">
              <h4>
                {lftext('pages.device_detail.current_telemetry')}
              </h4>
              <dl className="key_value_pairs">
                <div className="key_value_pairs__pair">
                  <dt className="key_value_pairs__pair_key">
                    {lftext('pages.device_detail.dry_temperature')}
                  </dt>
                  <dd className="key_value_pairs__pair_value  key_value_pairs__pair_value--large">
                    {
                      recentTemperatureTelemetryDataLoading &&
                      <>
                        {lftext('common.loading')}
                      </>
                    }
                    {
                      !recentTemperatureTelemetryDataLoading &&
                      recentTemperatureTelemetryDataError &&
                      <>Err: { recentTemperatureTelemetryDataError }</>
                    }
                    {
                      !recentTemperatureTelemetryDataLoading &&
                      (
                        !recentTemperatureTelemetryData ||
                        !recentTemperatureTelemetryData?.dry?.temperature?.current
                      ) &&
                      <span className="dimmed_text">
                        {lftext('common.not_available')}
                      </span>
                    }
                    {
                      !recentTemperatureTelemetryDataLoading &&
                      !recentTemperatureTelemetryDataError &&
                      recentTemperatureTelemetryData &&
                      recentTemperatureTelemetryData?.dry?.temperature?.current &&
                      (
                        <>
                          <Icon className="key_value_pairs__pair_value_icon" name="temperature_dry" label="Dry temperature" size="l" />
                          { renderTemperature(recentTemperatureTelemetryData.dry.temperature.current) }
                        </>
                      )
                    }
                  </dd>
                </div>
                <div className="key_value_pairs__pair">
                  <dt className="key_value_pairs__pair_key">
                    {lftext('pages.device_detail.wet_bulb_temperature')}
                  </dt>
                  <dd className="key_value_pairs__pair_value  key_value_pairs__pair_value--large">
                  {
                      recentTemperatureTelemetryDataLoading &&
                      <>
                        {lftext('common.loading')}
                      </>
                    }
                    {
                      !recentTemperatureTelemetryDataLoading &&
                      recentTemperatureTelemetryDataError &&
                      <>Err: { recentTemperatureTelemetryDataError }</>
                    }
                    {
                      !recentTemperatureTelemetryDataLoading &&
                      (
                        !recentTemperatureTelemetryData ||
                        !recentTemperatureTelemetryData?.wetbulb?.temperature?.current
                      ) &&
                      <span className="dimmed_text">
                        {lftext('common.not_available')}
                      </span>
                    }
                    {
                      !recentTemperatureTelemetryDataLoading &&
                      !recentTemperatureTelemetryDataError &&
                      recentTemperatureTelemetryData &&
                      recentTemperatureTelemetryData?.wetbulb?.temperature?.current &&
                      (
                        <>
                          <Icon className="key_value_pairs__pair_value_icon" name="temperature_wetbulb" label="Wet bulb temperature" size="l" />
                          { renderTemperature(recentTemperatureTelemetryData.wetbulb.temperature.current) }
                        </>
                      )
                    }
                  </dd>
                </div>
                <div className="key_value_pairs__pair">
                  <dt className="key_value_pairs__pair_key">
                    {lftext('pages.device_detail.relative_humidity')}
                  </dt>
                  <dd className="key_value_pairs__pair_value  key_value_pairs__pair_value--large">
                  {
                      recentTemperatureTelemetryDataLoading &&
                      <>
                        {lftext('common.loading')}
                      </>
                    }
                    {
                      !recentTemperatureTelemetryDataLoading &&
                      recentTemperatureTelemetryDataError &&
                      <>Err: { recentTemperatureTelemetryDataError }</>
                    }
                    {
                      !recentTemperatureTelemetryDataLoading &&
                      (
                        !recentTemperatureTelemetryData ||
                        !recentTemperatureTelemetryData?.dry?.temperature?.current ||
                        !recentTemperatureTelemetryData?.wetbulb?.temperature?.current
                      ) &&
                      <span className="dimmed_text">
                        {lftext('common.not_available')}
                      </span>
                    }
                    {
                      !recentTemperatureTelemetryDataLoading &&
                      !recentTemperatureTelemetryDataError &&
                      recentTemperatureTelemetryData &&
                      relativeHumidity !== null &&
                      <>
                        <Icon className="key_value_pairs__pair_value_icon" name="relative_humidity" label="Relative humidity" size="l" />
                        { MathUtils.precisionRound(relativeHumidity * 100, 2) } %
                      </>
                    }
                  </dd>
                </div>
                <div className="key_value_pairs__pair">
                  <dt className="key_value_pairs__pair_key">
                    {lftext('pages.device_detail.timestamp')}
                  </dt>
                  <dd className="key_value_pairs__pair_value">
                    {
                      recentTemperatureTelemetryDataLoading &&
                      <>
                        {lftext('common.loading')}
                      </>
                    }
                    {
                      !recentTemperatureTelemetryDataLoading &&
                      recentTemperatureTelemetryDataError &&
                      <>Err: { recentTemperatureTelemetryDataError }</>
                    }
                    {
                      !recentTemperatureTelemetryDataLoading &&
                      !recentTemperatureTelemetryData?.dry &&
                      !recentTemperatureTelemetryData?.wetbulb &&
                      <span className="dimmed_text">
                        {lftext('common.not_available')}
                      </span>
                    }
                    {
                      !recentTemperatureTelemetryDataLoading &&
                      !recentTemperatureTelemetryDataError &&
                      recentTemperatureTelemetryData &&
                      (
                        recentTemperatureTelemetryData?.dry?.timestamp ||
                        recentTemperatureTelemetryData?.wetbulb?.timestamp
                      ) &&
                      <>{ new Date(recentTemperatureTelemetryData?.dry?.timestamp || recentTemperatureTelemetryData?.wetbulb?.timestamp).toLocaleString() }</>
                    }
                  </dd>
                </div>
              </dl>
              {
                !recentTemperatureTelemetryDataLoading &&
                !recentTemperatureTelemetryDataError &&
                recentTemperatureTelemetryData &&
                relativeHumidity !== null &&
                recentTemperatureTelemetryData.dry.temperature.current < recentTemperatureTelemetryData.wetbulb.temperature.current &&
                (
                  <div className="feedback_message  feedback_message--warning">
                    {lftext('pages.device_detail.wet_bulb_temp_higher_than_dry')}
                  </div>
                )
              }
            </div>
            <div className="summary_details__details">
              <h4>
                {lftext('pages.device_detail.history')}
              </h4>
              {
                connectedPeripheralsDataLoading ? (
                  <p>
                    {lftext('common.loading')}
                  </p>
                ) : connectedTemperatureSensors && connectedTemperatureSensors.length ? (
                  <div className="timebased_data">
                    <div className="timebased_data__toolbar">
                      <div className="timebased_data__scope_selection">
                        <div className="select_input  select_input--inline">
                          <label
                            className="select_input__label"
                            htmlFor="sensor-selection"
                          >
                            {lftext('pages.device_detail.select_sensor')}
                          </label>
                          <select
                            className="select_input__field"
                            id="sensor-selection"
                            onChange={ (e) => setSelectedTemperatureSensorDeviceId(e.target.value) }
                            defaultValue={ selectedTemperatureSensorDeviceId }
                          >
                            {
                              connectedTemperatureSensors.map(p => {
                                return (
                                  <option
                                    key={ `sensor-${p.deviceId}` }
                                    value={ p.deviceId }
                                  >
                                    { p.label } ({ getSensorTypeLabel(p.peripheralType) } - { getSensorStatusLabel(p.status) })
                                  </option>
                                );
                              })
                            }
                          </select>
                        </div>
                      </div>
                      <div className="timebased_data__period_selection">
                        <ul className="toolbar">
                          <li className="toolbar__item"><IconButton label="Previous 7 days" icon="navigate_before" onClick={ onNavigateBeforeClick } /></li>
                          {
                            timeRange && (
                              <li className="toolbar__item">
                                { new Date(timeRange.from).toLocaleDateString() } - { new Date(timeRange.to).toLocaleDateString() }
                              </li>
                            )
                          }
                          <li className="toolbar__item"><IconButton label="Next 7 days" icon="navigate_next" onClick={ onNavigateNextClick } disabled={ DateUtils.dateIsTodayOrLater(timeRange.to) } /></li>
                        </ul>
                      </div>
                    </div>
                    <div className="timebased_data__data">
                      {
                        chartLoading ? (
                          <div className="loader">
                            <p>
                              {lftext('common.loading')}
                            </p>
                          </div>
                        ) : (
                          chartError ? (
                            renderError(chartError)
                          ) : (
                            chartData && chartData.data.length ? (
                              <>
                                {
                                  chartLoading ? (
                                    <div className="loader">
                                      <p>
                                        {lftext('common.loading')}
                                      </p>
                                    </div>
                                  ) : (
                                    chartError ? (
                                      <div className="feedback_message  feedback_message--error">
                                        {lftext('pages.device_detail.errors.error_getting_chart_data')}
                                      </div>
                                    ) : (
                                      chartData && chartData.data.length ? (
                                        <TemperatureChart
                                          chartData={ chartData }
                                        />
                                      ) : (
                                        <p>
                                          {lftext('pages.device_detail.errors.no_data_for_period')}
                                        </p>
                                      )
                                    )
                                  )
                                }
                                <p className="callout  callout--instructions">
                                  {lftext('pages.device_detail.refresh_for_data')}
                                </p>
                              </>
                            ) : (
                              <p>{lftext('pages.device_detail.errors.no_data_for_period')}</p>
                            )
                          )
                        )
                      }
                    </div>
                  </div>
                ) : renderError({ type: "NO_SENSORS_FOR_UNIT" })
              }
            </div>
            <div className="summary_details__technical_info">
              <h4>
                {lftext('pages.device_detail.technical_info')}
              </h4>
              <dl className="key_value_pairs">
                <div className="key_value_pairs__pair">
                  <dt className="key_value_pairs__pair_key">
                    {lftext('pages.device_detail.type')}
                  </dt>
                  <dd className="key_value_pairs__pair_value">{ unitTypeLabel }</dd>
                </div>
                <div className="key_value_pairs__pair">
                  <dt className="key_value_pairs__pair_key">
                    {lftext('pages.device_detail.hardware_id')}
                  </dt>
                  <dd className="key_value_pairs__pair_value">{ unit.deviceId }</dd>
                </div>
                <div className="key_value_pairs__pair">
                  <dt className="key_value_pairs__pair_key">
                    {lftext('pages.device_detail.hardware_version')}
                  </dt>
                  <dd className="key_value_pairs__pair_value">{ unit.hardwareVersion }</dd>
                </div>
                <div className="key_value_pairs__pair">
                  <dt className="key_value_pairs__pair_key">
                    {lftext('pages.device_detail.firmware_version')}
                  </dt>
                  <dd className="key_value_pairs__pair_value">{ unit.firmwareVersion }</dd>
                </div>
              </dl>
            </div>
          </article>
        )
      }
    </>
  );
}

GatewayDetailConnectedUnitDetails.propTypes = {
  gateway: PropTypes.object.isRequired,
  unit: PropTypes.object.isRequired
};

export default GatewayDetailConnectedUnitDetails;
